"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { ArrowLeftRight, Check, Eye, Loader2, Lock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { GenerationWithVehicle } from "@/hooks/use-generations";
import { useCheckout } from "@/hooks/use-checkout";
import {
  PREVIEW_CTA_PACK_ID,
  getEffectiveCredits,
  getPackPriceUsd,
  type CreditPackId,
} from "@/lib/payments/client";
import { MiniPreviewOffer } from "@/components/app/mini-preview-offer";
import { FREE_PREVIEW_OFFER_VARIANT } from "@/lib/payments/free-preview-config";
import { toast } from "sonner";
import {
  getComparisonPrimaryAction,
} from "@/lib/free-preview-funnel";
import {
  trackFreePreviewOfferShown,
  trackFreePreviewPaidCtaClicked,
  trackFreePreviewComparisonViewed,
  trackFreePreviewWinnerSelected,
} from "@/lib/posthog-client";

interface FreePreviewComparisonProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  previews: GenerationWithVehicle[];
  onOpenPreview: (generation: GenerationWithVehicle) => void;
  creditBalance: number;
  isUnlocking: boolean;
  onUnlockPreview: (generation: GenerationWithVehicle) => Promise<void>;
}

function getWheelName(generation: GenerationWithVehicle) {
  return generation.userRim?.name || generation.rim?.name || "Saved wheel";
}

function ComparisonCard({
  generation,
  selected,
  onSelect,
}: {
  generation: GenerationWithVehicle;
  selected: boolean;
  onSelect: () => void;
}) {
  const wheel = generation.userRim || generation.rim;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "group overflow-hidden rounded-2xl border bg-zinc-950 text-left transition-colors",
        selected ? "border-blue-400 ring-2 ring-blue-500/20" : "border-white/10 hover:border-white/25"
      )}
      aria-pressed={selected}
    >
      <div className="relative aspect-4/3 bg-black">
        <Image
          src={generation.resultImageUrl!}
          alt={`${getWheelName(generation)} wheel preview`}
          fill
          sizes="(max-width: 1024px) 100vw, 50vw"
          className="object-contain"
        />
        {selected ? (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-blue-500 px-2.5 py-1 text-xs font-semibold text-white">
            <Check className="h-3 w-3" /> My pick
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-3 p-3">
        {wheel ? (
          <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-white/10 bg-white/5">
            <Image src={wheel.imageUrl} alt="" fill sizes="40px" className="object-cover" />
          </div>
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-white">{getWheelName(generation)}</p>
          <p className="mt-0.5 text-xs text-zinc-500">Tap to choose this look</p>
        </div>
      </div>
    </button>
  );
}

export function FreePreviewComparison({
  open,
  onOpenChange,
  previews,
  onOpenPreview,
  creditBalance,
  isUnlocking,
  onUnlockPreview,
}: FreePreviewComparisonProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const trackedComparisonKeyRef = useRef<string | null>(null);
  const checkout = useCheckout();
  const ctaPackPrice = getPackPriceUsd(PREVIEW_CTA_PACK_ID);
  const paidPreviewsAfterUnlock = Math.max(
    getEffectiveCredits(PREVIEW_CTA_PACK_ID) - 1,
    0
  );
  const comparisonKey = previews.map((preview) => preview.id).join(":");

  useEffect(() => {
    if (!open || previews.length === 0) return;
    queueMicrotask(() => setSelectedId((current) => current || previews[0].id));
  }, [open, previews]);

  useEffect(() => {
    if (!open || previews.length < 2 || trackedComparisonKeyRef.current === comparisonKey) return;
    trackedComparisonKeyRef.current = comparisonKey;
    trackFreePreviewComparisonViewed({
      generationIds: previews.slice(0, 2).map((preview) => preview.id),
      vehicleUploadId: previews[0]?.vehicleUploadId,
    });
    trackFreePreviewOfferShown({
      generationId: previews[0]?.id,
      previewNumber: 2,
      remainingFreePreviews: 0,
      source: "free_preview_comparison",
      offerVariant: FREE_PREVIEW_OFFER_VARIANT,
    });
  }, [comparisonKey, open, previews]);

  const selectedPreview = previews.find((preview) => preview.id === selectedId) || previews[0];
  const primaryAction = getComparisonPrimaryAction(creditBalance);
  const canUnlock = primaryAction === "unlock";

  const handleSelectPreview = (preview: GenerationWithVehicle) => {
    if (preview.id === selectedPreview?.id) return;
    setSelectedId(preview.id);
    trackFreePreviewWinnerSelected({
      generationId: preview.id,
      previousGenerationId: selectedPreview?.id,
      wheelType: preview.userRim ? "custom" : preview.rim ? "catalog" : "deleted",
    });
  };

  const handlePrimaryAction = async (packId: CreditPackId = PREVIEW_CTA_PACK_ID) => {
    if (!selectedPreview) return;

    if (canUnlock) {
      await onUnlockPreview(selectedPreview);
      return;
    }

    trackFreePreviewPaidCtaClicked({
      packId,
      generationId: selectedPreview.id,
      previewNumber: 2,
      remainingFreePreviews: 0,
      source: "free_preview_comparison",
      offerVariant: FREE_PREVIEW_OFFER_VARIANT,
    });
    checkout.mutate(
      {
        packId,
        selectedGenerationId: selectedPreview.id,
        source: "free_preview_comparison",
        previewNumber: 2,
      },
      {
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : "Failed to start checkout");
        },
      }
    );
  };

  if (previews.length < 2) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-white/10 bg-zinc-950 p-0 sm:max-w-5xl [&>button]:hidden">
        <DialogHeader className="border-b border-white/10 px-4 pb-4 pt-5 sm:px-6">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-500/15 text-blue-400">
              <ArrowLeftRight className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle>Compare your two free previews</DialogTitle>
              <DialogDescription className="mt-1">Choose the wheel you would take further.</DialogDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} aria-label="Close comparison">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="p-4 lg:p-6">
          <div className="mb-3 grid grid-cols-2 gap-2 lg:hidden">
            {previews.slice(0, 2).map((preview, index) => (
              <button
                key={preview.id}
                type="button"
                onClick={() => handleSelectPreview(preview)}
                className={cn(
                  "rounded-xl border px-3 py-2 text-left transition-colors",
                  preview.id === selectedPreview?.id
                    ? "border-blue-400 bg-blue-500/15 text-white"
                    : "border-white/10 bg-white/5 text-zinc-400"
                )}
              >
                <span className="block text-[10px] font-semibold uppercase tracking-wider text-blue-300">Option {index + 1}</span>
                <span className="mt-0.5 block truncate text-xs font-medium">{getWheelName(preview)}</span>
              </button>
            ))}
          </div>

          <div className="lg:hidden">
            {selectedPreview ? (
              <ComparisonCard
                generation={selectedPreview}
                selected
                onSelect={() => undefined}
              />
            ) : null}
          </div>

          <div className="hidden gap-3 lg:grid lg:grid-cols-2">
            {previews.slice(0, 2).map((preview) => (
              <ComparisonCard
                key={preview.id}
                generation={preview}
                selected={preview.id === selectedPreview?.id}
                onSelect={() => handleSelectPreview(preview)}
              />
            ))}
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-white/10 bg-zinc-950/95 p-4 backdrop-blur sm:px-6">
          <Button
            size="lg"
            className="h-12 w-full"
            onClick={() => handlePrimaryAction()}
            disabled={!selectedPreview || isUnlocking || checkout.isPending}
          >
            {isUnlocking || checkout.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {checkout.isPending ? "Opening checkout..." : "Unlocking..."}
              </>
            ) : canUnlock ? (
              <>
                <Lock className="mr-2 h-4 w-4" />
                Unlock my pick · 1 credit
              </>
            ) : (
              <>Unlock my pick + {paidPreviewsAfterUnlock} more · ${ctaPackPrice.toFixed(2)}</>
            )}
          </Button>
          {!canUnlock && (
            <MiniPreviewOffer
              onPurchase={() => handlePrimaryAction("mini")}
              disabled={!selectedPreview || isUnlocking || checkout.isPending}
            />
          )}
          <Button
            variant="ghost"
            className="mt-1 h-10 w-full text-zinc-400"
            onClick={() => selectedPreview && onOpenPreview(selectedPreview)}
            disabled={!selectedPreview}
          >
            <Eye className="mr-2 h-4 w-4" /> View selected preview
          </Button>
          <p className="mt-2 text-center text-[11px] text-zinc-500">Your previews stay saved in My previews.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
